import React from 'react';
import { Stack, Tooltip } from '@mui/material';
import dayjs from 'dayjs';
import {
  StyledCellText,
  StyledCellText2,
} from 'molecules/Table/styles/Text.styles';
import { LatestBulkJob } from 'organisms/CloudOpsReportAdmin/types';
import { neutrals } from 'utils/styles/color';
import { palette } from 'utils/styles/variables';

type StatusPresentation = {
  label: string;
  color: string;
};

const knownStatusPresentations: Record<string, StatusPresentation> = {
  pending: { label: 'Pending', color: palette.primary.main },
  skipped_locked: {
    label: 'Skipped (locked)',
    color: palette.warning[600],
  },
  succeeded: { label: 'Succeeded', color: palette.success[600] },
  failed: { label: 'Failed', color: palette.error[450] },
};

const humanize = (value: string) => {
  const normalized = value.replace(/_/g, ' ').trim();
  return normalized
    ? `${normalized.charAt(0).toUpperCase()}${normalized.slice(1)}`
    : 'Unknown';
};

export const getLatestBulkJobStatusPresentation = (
  status: string
): StatusPresentation =>
  Object.prototype.hasOwnProperty.call(knownStatusPresentations, status)
    ? knownStatusPresentations[status]
    : {
        label: humanize(status),
        color: neutrals[700] as string,
      };

const isoTimestampPattern =
  /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.\d+)?Z?$/;

const formatTimestamp = (value?: string | null) => {
  if (!value) return null;
  const match = isoTimestampPattern.exec(value);
  if (!match) return null;

  const [
    ,
    yearValue,
    monthValue,
    dayValue,
    hourValue,
    minuteValue,
    secondValue,
  ] = match;
  const year = Number(yearValue);
  const month = Number(monthValue);
  const day = Number(dayValue);
  const hour = Number(hourValue);
  const minute = Number(minuteValue);
  const second = Number(secondValue);
  const isLeapYear = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
  const daysInMonth = [
    31,
    isLeapYear ? 29 : 28,
    31,
    30,
    31,
    30,
    31,
    31,
    30,
    31,
    30,
    31,
  ];

  if (
    year < 1 ||
    month < 1 ||
    month > 12 ||
    day < 1 ||
    day > daysInMonth[month - 1] ||
    hour > 23 ||
    minute > 59 ||
    second > 59
  ) {
    return null;
  }

  const timestamp = dayjs(value);
  return timestamp.isValid()
    ? timestamp.format('MMM D, YYYY [at] HH:mm')
    : null;
};

export const LatestBulkJobStatus = ({
  job,
}: {
  job?: LatestBulkJob | null;
}) => {
  if (!job) {
    return <StyledCellText $textType="default">—</StyledCellText>;
  }

  const actionLabel = humanize(job.action);
  const status = getLatestBulkJobStatusPresentation(job.item_status);
  const displayLabel = `${actionLabel} ${status.label.toLowerCase()}`;
  const createdAt = formatTimestamp(job.created_at);
  const finishedAt = formatTimestamp(job.finished_at);

  const tooltip = (
    <Stack>
      <span>{displayLabel}</span>
      {job.created_by_name && (
        <span>{`Triggered by ${job.created_by_name}`}</span>
      )}
      {createdAt && <span>{`Triggered at ${createdAt}`}</span>}
      {finishedAt && <span>{`Finished at ${finishedAt}`}</span>}
    </Stack>
  );

  return (
    <Tooltip title={tooltip} arrow>
      <Stack
        tabIndex={0}
        aria-label={`${displayLabel} bulk action details`}
        sx={{ width: 'fit-content' }}
      >
        <Stack direction="row" alignItems="center" spacing={0.75}>
          {/* <Box
            component="span"
            data-testid="latest-bulk-job-status-indicator"
            aria-hidden="true"
            sx={{
              width: 8,
              height: 8,
              flexShrink: 0,
              borderRadius: '50%',
              backgroundColor: status.color,
            }}
          /> */}
          <StyledCellText $textType="default" $color={neutrals[700]}>
            {displayLabel}
          </StyledCellText>
        </Stack>
        {job.created_by_name && (
          <StyledCellText2>{`by ${job.created_by_name}`}</StyledCellText2>
        )}
      </Stack>
    </Tooltip>
  );
};
